import { useState } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '../components/icons';
import { addDays, dayKey, startOfDay, startOfWeek } from './dates';

/**
 * A small month for jumping to a date. Days with events are marked; the
 * span the main view shows is highlighted.
 */
export function MiniMonth({
  selected,
  shown,
  busy,
  onPick,
}: {
  selected: Date;
  shown: { start: Date; end: Date };
  busy: ReadonlySet<string>;
  onPick: (day: Date) => void;
}): JSX.Element {
  const [month, setMonth] = useState(
    () => new Date(selected.getFullYear(), selected.getMonth(), 1),
  );
  const first = startOfWeek(month);
  const today = dayKey(new Date());
  return (
    <div className="mini-month">
      <div className="mini-head">
        <strong>{month.toLocaleDateString([], { month: 'long', year: 'numeric' })}</strong>
        <button
          type="button"
          className="icon-btn"
          aria-label="Previous month"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
        >
          <ChevronLeftIcon />
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label="Next month"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
        >
          <ChevronRightIcon />
        </button>
      </div>
      <div className="mini-grid" role="grid" aria-label="Choose a date">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <span key={i} className="mini-dow" aria-hidden="true">
            {d}
          </span>
        ))}
        {Array.from({ length: 42 }, (_, i) => addDays(first, i)).map((d) => {
          const key = dayKey(d);
          const inView = d >= startOfDay(shown.start) && d < shown.end;
          return (
            <button
              type="button"
              key={key}
              className={[
                'mini-day',
                d.getMonth() !== month.getMonth() ? 'other' : '',
                key === today ? 'today' : '',
                inView ? 'in-view' : '',
                busy.has(key) ? 'has' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-label={d.toLocaleDateString([], {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
              onClick={() => onPick(d)}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
